// Onde o token de acesso vive: na memória da aba, e em nenhum outro lugar.
//
// Antes ele ficava no localStorage. O problema não é teórico: qualquer script
// que consiga rodar na origem do sistema — um `.svg` ou `.html` enviado como
// anexo, uma dependência do frontend comprometida — lê o localStorage e sai
// com um token válido para copiar. Em memória, o roubo exige a aba aberta e
// morre quando ela fecha.
//
// A sessão não se perde ao recarregar: o refresh token continua num cookie
// httpOnly (`jrt`), que o JavaScript não enxerga. Ao abrir a página, o
// AuthProvider troca esse cookie por um token novo — que é justamente o
// caminho que já existia.
//
// Uma aba não enxerga o token da outra, e é assim que deve ser: cada aba
// restaura o seu pelo cookie.

let tokenEmMemoria = null;

// Quem precisa saber que o token mudou (hoje, o socket, que só o verifica no
// handshake e ficaria com o antigo depois de uma renovação).
const ouvintes = new Set();

export const getToken = () => tokenEmMemoria;

export const setToken = token => {
	tokenEmMemoria = token || null;
	ouvintes.forEach(ouvinte => {
		try {
			ouvinte(tokenEmMemoria);
		} catch {
			// Um ouvinte quebrado não pode impedir os outros de saber.
		}
	});
};

export const clearToken = () => setToken(null);

// Apaga TODO vestigio visivel ao JS (cookies + localStorage + sessionStorage)
// pertencente a origem atual. Chamado em: handleLogout() e em encerrarSessao()
// (expiração do token / kickout). NUNCA mais acontece de, entrando em outra
// empresa/usuario, aparecer ticket ou nome da sessão anterior.
//
// Preserva somente os cookies que sao do navegador (httpOnly) — como `jrt`,
// que eh limpo por outro lado: `res.clearCookie("jrt")` no backend (pois JS
// nao consegue ler/remover httpOnly). Ou seja: o `jrt` vai embora no DELETE
// /auth/logout; o resto vai embora por aqui.
export const wipeAllBrowserState = () => {
	// 1) Cookies client-side (nao httpOnly). Passamos por todos os dominios e
	// paths candidatos (path=/, path= atual, path= sem nada), exatamente como
	// gravarPreferencia/terceiros gravam, para garantir que algum cookie
	// "fantasma" nao sobreviva apos path diferente.
	try {
		const partes = document.cookie.split(";");
		const nomes = [];
		for (const parte of partes) {
			const [nomeCru] = parte.trim().split("=");
			if (nomeCru) nomes.push(decodeURIComponent(nomeCru));
		}

		const paths = ["", "/"];
		const dominios = [""];

		// Tenta dominios correntes com subdominios para cookies de SSO antigos.
		try {
			const host = window.location.hostname;
			if (host && !dominios.includes(host)) dominios.push(host);
			const pedacos = host.split(".");
			if (pedacos.length >= 2) {
				const dominioPai = pedacos.slice(-2).join(".");
				if (dominioPai && !dominios.includes(dominioPai)) dominios.push(dominioPai);
			}
		} catch { /* noop */ }

		for (const nome of nomes) {
			for (const dominio of dominios) {
				for (const path of paths) {
					const partes = [
						`${encodeURIComponent(nome)}=`,
						"expires=Thu, 01 Jan 1970 00:00:00 GMT",
						"max-age=-1",
						path ? `path=${path}` : "path=/",
					];
					if (dominio) partes.push(`domain=${dominio}`);
					document.cookie = partes.join("; ");
					document.cookie = `${encodeURIComponent(nome)}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path || "/"}`;
				}
			}
		}
	} catch { /* noop */ }

	// 2) localStorage e sessionStorage (tudo, sem excecao — inclui os de debug
	// do chat interno, Intelligence L958 localStorage "user", FlowBuilder e
	// qualquer outro que apareceu depois).
	try {
		window.localStorage && window.localStorage.clear();
	} catch { /* noop */ }
	try {
		window.sessionStorage && window.sessionStorage.clear();
	} catch { /* noop */ }

	// 3) Cache do service worker e indexedDB eh caro de apagar e nao eh
	// problema aqui — o que causa "ticket da empresa A logado empresa B"
	// sao estados do React recarregados de storage e tokens antigos.
};

export const onTokenChange = ouvinte => {
	ouvintes.add(ouvinte);
	return () => ouvintes.delete(ouvinte);
};
